import { CheckBadgeIcon } from "@heroicons/react/24/outline";
import { ChecklistProps } from "./Checklists.js";
import { motion } from "motion/react";
import { type GetResourceAgendaChecklistsResponse } from "@swivu/client-api";
import { cn } from "../../utils/cn.js";
import { useIsGummy } from "../../hooks/useGummyTheme.js";

type ChecklistHeaderProps = {
  checklist:
    | ChecklistProps["checklist"]
    | GetResourceAgendaChecklistsResponse[number];
  itemCount: number;
  completeCount: number;
  className?: string;
};

const ChecklistHeader = ({
  checklist,
  className,
  itemCount,
  completeCount
}: ChecklistHeaderProps) => {
  const pct = Math.round(((completeCount ?? 0) / (itemCount ?? 1)) * 100);
  const isGummy = useIsGummy();

  return (
    <div
      style={{
        viewTransitionName: `checklistHeader-${checklist.id}`
      }}
      className={cn(
        "mobile-pref-checklist-header relative z-0 -mx-2 block space-y-3 px-2 py-4 md:py-8",
        isGummy
          ? "bg-transparent"
          : "bg-mono-200 dark:bg-mono-950 dark:border-mono-700/50",
        className
      )}
    >
      <div className="mobile-pref-checklist-header-row flex items-end justify-between gap-x-6">
        <div className="flex-1">
          <h2
            className={cn(
              "mobile-pref-section-title text-lg",
              isGummy && "text-[24px] font-extrabold leading-6"
            )}
          >
            {checklist?.checklist_title || "Checklist Item"}
          </h2>
          {checklist?.entity_title && (
            <p
              className={cn(
                "mobile-pref-card-meta",
                isGummy
                  ? "text-lg mt-2 font-extrabold leading-5 text-mono-500 dark:text-mono-400"
                  : "opacity-60"
              )}
            >
              {checklist?.entity_title}
            </p>
          )}
        </div>
        {completeCount !== itemCount ? (
          <p
            className={cn(
              "mobile-pref-card-meta flex-none leading-5",
              isGummy
                ? "text-sm font-extrabold text-mono-600 dark:text-mono-300"
                : "opacity-60"
            )}
          >
            {completeCount || 0} of {itemCount}
          </p>
        ) : (
          <div
            className={cn(
              "mobile-pref-checklist-complete-badge flex flex-none items-center gap-2 rounded-full",
              isGummy
                ? cn(
                    "bg-tile-success p-1",
                    "shadow-[0_6px_14px_-6px_rgba(16,185,129,0.5),inset_0_2px_0_0_rgba(255,255,255,0.5)]"
                  )
                : "bg-emerald-400 shadow dark:bg-emerald-400"
            )}
          >
            <CheckBadgeIcon
              className={cn(
                "mobile-pref-checklist-complete-icon size-7",
                isGummy
                  ? "text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.25)]"
                  : "text-mono-100 dark:text-mono-900"
              )}
            />
          </div>
        )}
      </div>
      <div
        className={cn(
          "mobile-pref-checklist-progress relative h-3 w-full overflow-hidden",
          isGummy
            ? cn(
                "rounded-full",
                "bg-mono-200/60 dark:bg-mono-900",
                "shadow-[inset_0_2px_4px_0_rgba(0,0,0,0.12),inset_0_-1px_0_0_rgba(255,255,255,0.5)]",
                "dark:shadow-[inset_0_2px_4px_0_rgba(0,0,0,0.5),inset_0_-1px_0_0_rgba(255,255,255,0.05)]"
              )
            : cn(
                "rounded-md border dark:border-emerald-700/50",
                pct === 0
                  ? "border-mono-300 dark:border-mono-700/50"
                  : "border-emerald-400 dark:border-emerald-800"
              )
        )}
      >
        <motion.div
          initial={false}
          className={cn(
            "absolute h-full min-w-0 transition-colors",
            isGummy
              ? cn(
                  "rounded-full bg-tile-success",
                  "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.5)]"
                )
              : pct === 100
                ? "bg-emerald-300 dark:bg-emerald-400"
                : "bg-emerald-200 dark:bg-emerald-600"
          )}
          transition={{ type: "spring", bounce: 0, duration: 0.5 }}
          animate={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export default ChecklistHeader;
